import { useEffect, useRef, useMemo } from "react"
import {
    Link,
    useLocation,
    useNavigate,
    useOutletContext,
    useSearchParams,
} from "react-router-dom"

import moment from "moment"
import Delete from "pages/product/supplier/delete"
import Filter from "pages/product/supplier/filter"
import Import from "pages/product/supplier/import"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { CloudDownload, Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useProductSupplierRepositories from "repositories/product-supplier"
import useSupplierRepositories from "repositories/supplier"
import Table from "components/table"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Logged in user ability
    const { can, isSuper } = useAuth()

    // Configuration (kalau dibutuhkan)
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get("page")
    const search = searchParams.get("search")
    const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const name = searchParams.get("name")
    const from = searchParams.get("from")
    const to = searchParams.get("to")
    const supplier_id = searchParams.get("supplier_id")

    const orderBy = searchParams.get("order_by")
    const direction = searchParams.get("direction")

    const {
        data: productSupplierData,
        isLoading: isLoadingProductSupplierData,
        mutate: mutateProductSupplierData,
    } = useProductSupplierRepositories({
        page,
        search,
        name,
        from,
        to,
        supplier_id,
        order_by: orderBy,
        direction,
    })

    // ambil semua supplier untuk dicocokkan dengan supplier_id
    const {
        data: supplierSelection,
        isLoading: isLoadingSupplierSelection,
    } = useSupplierRepositories({ paginate: false })

    const supplierById = useMemo(() => {
        const map = new Map()
        ;(supplierSelection || []).forEach((s) => {
            map.set(s.id, s)
        })
        return map
    }, [supplierSelection])

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition?.reverse) {
            setTransition({ type: "slide", reverse: false })
        }
    }, [])

    useEffect(() => {
        if (searchInput.current) {
            searchInput.current.focus()
        }
    }, [search])

    useEffect(() => {
        if (search === "") {
            navigate("", { replace: true })
        }

        if (isLoadingProductSupplierData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [productSupplierData, isLoadingProductSupplierData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === "desc") || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: "asc",
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: "desc",
            })
        }
    }

    // Headers tabel
    const dataListHeaders = [
        {
            isAction: false,
            content: "Nama Supplier",
            sortKey: "supplier_id",
        },
        {
            isAction: false,
            content: "Product Model",
            sortKey: "product_model_id", // atau "product_model.name" kalau backend support
        },
        // {
        //     isAction: false,
        //     content: "Supplier ID",
        //     sortKey: "supplier_id",
        // },
        {
            isAction: false,
            content: lang.created_at,
            sortKey: "created_at",
        },
        {
            isAction: false,
            content: lang.updated_at,
            sortKey: "updated_at",
        },
        {
            isAction: true,
            content: null,
        },
    ]

    // Kolom tabel
    const dataListColumns = [
        // Nama supplier (ambil dari repository supplier)
        {
            type: "default",
            bold: true,
            content: (value) => {
                const supplier = supplierById.get(value.supplier_id)
                return supplier?.name ?? `ID: ${value.supplier_id}`
            },
        },
        // Product model name
        {
            type: "default",
            bold: true,
            content: (value) => {
                // support beberapa bentuk response:
                // 1. product_models: [] (array)
                const models = value.product_models || value.productModels
                if (Array.isArray(models) && models.length > 0) {
                    return models
                        .map((m) => m.name || m.long_name || `ID: ${m.id}`)
                        .join(", ")
                }

                // 2. product_model: {} (single)
                const model = value.product_model || value.productModel
                if (model) {
                    return model.name || model.long_name || `ID: ${model.id}`
                }

                // 3. fallback ke product_model_id
                if (value.product_model_id) {
                    return `ID: ${value.product_model_id}`
                }

                return "-"
            },
        },
        // Supplier ID
        // {
        //     type: "default",
        //     bold: false,
        //     content: (value) => value.supplier_id ?? "-",
        // },
        // Created at
        {
            type: "default",
            bold: false,
            content: (value) =>
                value.created_at
                    ? moment(value.created_at).format("MMMM D, YYYY")
                    : "-",
        },
        // Updated at
        {
            type: "default",
            bold: false,
            content: (value) =>
                value.updated_at
                    ? moment(value.updated_at).format("MMMM D, YYYY")
                    : "-",
        },
        // Action
        {
            type: "action",
            content: (value) => (
                <>
                    {/* {can("product-supplier.update") && (
                        <Link
                            to={`${value.id}/edit`}
                            state={{
                                back: title,
                                from: location?.pathname,
                                transition: "slide",
                            }}
                            className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
                        >
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )} */}

                    {can("product-supplier.delete") && (
                        <Delete
                            data={value}
                            onSuccess={() => mutateProductSupplierData()}
                        />
                    )}
                </>
            ),
        },
    ]

    const data = productSupplierData?.data ?? []
    const isEmpty =
        (data?.length ?? 0) === 0 &&
        !search &&
        !isLoadingProductSupplierData
    const isEmptyOnSearching =
        (data?.length ?? 0) === 0 &&
        !!search &&
        !isLoadingProductSupplierData

    const canExport = isSuper() || can('product-supplier.export')
    
    return (
        <div className="space-y-6">
            {/* Header: search + filter + create button */}
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    {/* Search box */}
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                strokeWidth={1}
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                ></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line
                                    x1={21}
                                    y1={21}
                                    x2={15}
                                    y2={15}
                                ></line>
                            </svg>
                        </div>
                        <input
                            ref={searchInput}
                            onChange={(e) => setSearchTemp(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
                            value={searchTemp}
                            type="text"
                            placeholder={`${lang.search} Product Supplier`}
                            autoComplete="off"
                            className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        />
                    </div>

                    {/* Filter (name + date range) */}
                    <Filter
                        onSubmit={updateFilter}
                        onRemove={removeFilter}
                        data={Object.fromEntries(
                            Object.entries({ name, from, to,supplier_id }).filter(
                                ([_, v]) => v != null && v !== ""
                            )
                        )}
                    />
                    {can('product-supplier.import') && <Import onSuccess={() => mutateProductSupplierData()} />}
                </div>

                <div className="flex items-center space-x-2">
                    {can("product-supplier.create") && (
                        <div>
                            <Link
                                to="create"
                                onClick={() => {
                                    setTransition({
                                        type: "slide",
                                        reverse: true,
                                    })
                                }}
                                state={{
                                    back: title,
                                    from: location?.pathname,
                                    transition: { type: "slide" },
                                }}
                                className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                            >
                                <Plus
                                    className="w-4 h-4"
                                    stroke={1.5}
                                />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                    { can('product-supplier.export') && <div className="flex items-center space-x-2">
                        <button
                        type="button"
                        disabled={!canExport}
                        onClick={() => {
                            window.open(
                                `${process.env.REACT_APP_INVENTORY_API_URL}/product/supplier/export`,
                                "_blank"
                            )
                        }}
                        className={[
                            "inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 text-white bg-brand-800 rounded-xl active:hover:scale-90",
                            !canExport ? "opacity-50 cursor-not-allowed" : "",
                        ].join(" ")}
                        title={!canExport ? "Pilih outlet dulu untuk ekspor" : "Ekspor Excel"}
                        >
                        <CloudDownload className="w-4 h-4" stroke={1.5} />
                        <span>Ekspor Excel</span>
                        </button>
                    </div>}
                </div>
            </div>

            {/* Tabel list */}
            <Table
                isLoading={
                    isLoadingProductSupplierData || isLoadingSupplierSelection
                }
                data={data}
                headers={dataListHeaders}
                columns={dataListColumns}
                key={(value) => value.id}
                isEmpty={isEmpty}
                isEmptyOnSearching={isEmptyOnSearching}
                toggleSort={toggleSort}
                orderBy={orderBy}
                direction={direction}
            />

            {/* Pagination */}
            <Pagination
                links={productSupplierData?.links}
                from={productSupplierData?.from}
                to={productSupplierData?.to}
                total={productSupplierData?.total}
            />
        </div>
    )
}
